<template>
  <div>
    <v-dialog
      id="computer-modal-form"
      ref="computerModalForm"
      v-model="$store.state.computerModalFormOpen"
      width="800"
      :disabled="!isPermittedAll(['ComputersGeyByID'])"
      persistent
      no-click-animation>
      <form
        @submit.prevent="saveComputer">
        <v-container
          class="px-5 pb-2 pt-0">
          <!-- Title -->
          <v-row>
            <v-col
              class="d-flex pr-0">
              <h2>
                <span
                  v-if="$store.getters.isActiveComputerDirty">
                  *
                </span>
                <span
                  v-if="activeComputer.ComputerID">
                  Edit
                </span>
                <span
                  v-else>
                  Add
                </span>
                Computer
                <span
                  v-if="activeComputer.ComputerID">
                  #{{activeComputer.ComputerID}}
                </span>
              </h2>
              <v-btn
                class="square ml-auto"
                type="button"
                text
                @click="closeComputerModal()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <!-- / Title -->

          <!-- Serial Number / Dongle -->
          <v-row>
            <v-col>
              <v-text-field
                id="computer-serial-number"
                name="computerSerialNumber"
                ref="computerSerialNumber"
                v-model="activeComputer.SerialNumber"
                label="Serial Number:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                id="computer-dongle"
                name="computerDongle"
                ref="computerDongle"
                v-model="activeComputer.ComputerDongle"
                label="Dongle:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Serial Number / Dongle -->

          <!-- Product Key / Registration -->
          <v-row>
            <v-col :cols="isMobile ? 12 : 'default'">
              <v-text-field
                id="computer-product-key"
                name="computerProductKey"
                ref="computerProductKey"
                v-model="activeComputer.ProductKey"
                label="Product Key:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col :cols="isMobile ? 10 : 'default'">
              <v-text-field
                id="computer-registration-key"
                name="computerRegistrationKey"
                ref="computerRegistrationKey"
                v-model="activeComputer.RegistrationKey"
                label="Registration:"
                hide-details>
              </v-text-field>
            </v-col>
            <v-col
              cols="auto"
              class="pl-0">
              <v-btn
                class="square mt-6"
                title="Generate Key"
                type="button"
                color="primary"
                block
                :disabled="$store.getters.isLoadingActive"
                @click="generateKey">
                <v-icon>mdi-key</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <!-- / Product Key / Registration -->

          <!-- Software / SO Number -->
          <v-row>
            <v-col>
              <Autocomplete
                id="computer-software-installed"
                v-model="softwareType"
                label="Software:"
                :items="$store.state.softwareTypes"
                item-value="SoftwareTypeID"
                item-text="SoftwareTypeName">
              </Autocomplete>
            </v-col>
            <v-col>
              <v-text-field
                id="computer-so-number"
                name="computerSONumber"
                ref="computerSONumber"
                v-model="activeComputer.SONumber"
                label="SO Number:"
                :readonly="$store.getters.isLoadingActive"
                hide-details>
              </v-text-field>
            </v-col>
          </v-row>
          <!-- / Software / SO Number -->

          <!-- Contract End Date / Purchased From -->
          <v-row>
            <v-col :cols="isMobile ? 12 : 'default'">
              <Autocomplete
                id="computer-purchased-from"
                v-model="purchasingVendor"
                label="Purchased From:"
                :items="$store.state.purchasingVendors"
                item-value="PurchasingVendorID"
                item-text="PurchasingVendorName">
              </Autocomplete>
            </v-col>
          </v-row>
          <!-- / Contract End Date / Purchased From -->

          <!-- Support Contracts -->
          <v-row
            v-if="activeComputer.ComputerID
              && isPermittedAll([
                'SupportContractsGetAll'
              ])">
            <v-col>
              <ComputerSupportContractsFormTable
                :computer-id="activeComputer.ComputerID"
              />
            </v-col>
          </v-row>
          <!-- / Support Contracts -->

          <!-- Notes -->
          <v-row>
            <v-col>
              <label
                for="computer-notes">
                Notes:
              </label>
              <v-textarea
                id="computer-notes"
                name="computerNotes"
                class="pt-0 mt-0"
                rows="2"
                v-model="activeComputer.ComputerNotes"
                :readonly="$store.getters.isLoadingActive"
                hide-details
                no-resize>
              </v-textarea>
            </v-col>
          </v-row>
          <!-- / Notes -->

          <!-- Buttons -->
          <v-row>
            <v-col class="d-flex justify-end">
              <LastModifiedBy
                class="order-first ml-5"
                :entity="activeComputer"
              />
            </v-col>
         </v-row>
          <v-row>
            <v-col
              class="d-flex "
              :class="isMobile ? 'flex-column' : 'align-center'">
              <v-btn
                v-if="isPermittedToSave"
                class="ml-auto"
                type="submit"
                color="primary"
                :disabled="$store.getters.isLoadingActive
                  || !$store.getters.isActiveComputerDirty">
                Save
              </v-btn>

              <v-btn
                v-if="activeComputer.ComputerID
                  && isPermittedAll([
                    'ComputersDelete',
                  ])
                  && !activeComputer.ComputerIsDeleted"
                type="button"
                color="error"
                class="order-first "
                :class="isMobile ? 'mr-auto mb-2' : 'mr-5'"
                outlined
                @click="hideComputer"
                :disabled="$store.getters.isLoadingActive">
                Hide Computer
              </v-btn>

              <v-btn
                v-if="activeComputer.ComputerID
                  && isPermittedAll([
                    'ComputersDelete',
                  ])
                  && !activeComputer.ComputerIsDeleted"
                type="button"
                color="primary"
                class="order-first"
                :class="isMobile ? 'mr-auto mb-2' : 'mr-5'"
                outlined
                @click="openTransferComputerModal"
                :disabled="$store.getters.isLoadingActive">
                Transfer Computer
              </v-btn>

              <v-btn
                v-if="activeComputer.ComputerID
                  && isPermittedAll([
                    'ComputersRestore',
                  ])
                  && activeComputer.ComputerIsDeleted"
                type="button"
                color="error"
                class="order-first"
                :class="isMobile ? 'mr-auto mb-2' : 'mr-5'"
                outlined
                @click="restoreComputer"
                :disabled="$store.getters.isLoadingActive">
                Unhide Computer
              </v-btn>
            </v-col>
          </v-row>
          <!-- / Buttons -->
        </v-container>
      </form>
    </v-dialog>

    <v-dialog
      id="computer-transfer-modal-form"
      ref="computerTransferModalForm"
      v-model="isComputerTransferModalOpen"
      width="800"
      :disabled="!isPermittedAll(['ComputersGeyByID'])"
      persistent
      no-click-animation>
      <div class="container px-5 pb-2 pt-2">
        <h2 class="pb-5">Select a customer to transfer this computer to:</h2>
        <!-- Customer -->
          <v-col
            v-if="isPermittedAll([
              'CustomersGetAll',
            ])"
            >
            <Autocomplete
              id="transfer-computer-customer"
              label="Customer:"
              :items="allCustomers"
              v-model="customer"
              item-value="CustomerID"
              item-text="CustomerName">
              <template v-slot:item="{ item }">
                <div class="w-100 d-flex align-center">
                  <div class="mr-auto flex height-100 space-between width-50">
                    {{item.CustomerName}}
                  </div>
                  <p class="ended-support-contract margin-right-20"
                      v-if="item.CustomerStatusColor.CustomerStatusColorName == 'red'">
                      Do Not Support
                  </p>
                  <span
                    class="customer-status-dot"
                    :class="item.CustomerStatusColor.CustomerStatusColorName">
                  </span>
                </div>
              </template>
            </Autocomplete>
          </v-col>
        <!-- / Customer -->

        <!-- Buttons -->
          <v-row>
            <v-col
              class="d-flex align-center justify-end">
              <v-btn
                class="mr-5"
                type="submit"
                color="primary"
                @click="transferComputer">
                Save
              </v-btn>

              <v-btn
                type="button"
                color="error"
                class="order-first mr-5"
                outlined
                @click="closeTransferComputerModal">
                Close
              </v-btn>
            </v-col>
          </v-row>
        <!-- / Buttons -->
      </div>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import EventBus from '../event-bus';
import LastModifiedBy from './LastModifiedBy.vue';
import ComputerSupportContractsFormTable from './ComputerSupportContractsFormTable.vue';

export default {
  name: 'ComputerModalForm',
  components: {
    LastModifiedBy,
    ComputerSupportContractsFormTable,
  },
  data() {
    return {
      isComputerTransferModalOpen: false,
      customer: {
        CustomerStatusColor: { CustomerStatusColorName: '' },
      },
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isPermittedToSave() {
      return (!this.activeComputer.ComputerID
          && this.isPermittedAll(['ComputersCreate']))
        || (this.activeComputer.ComputerID
          && this.isPermittedAll(['ComputersUpdate']));
    },
    activeComputer() {
      return this.$store.state.activeComputer;
    },
    softwareType: {
      get() {
        return this.activeComputer.SoftwareType;
      },
      set(softwareType) {
        if (softwareType) {
          this.activeComputer.SoftwareType = _.cloneDeep(softwareType);
          this.activeComputer.SoftwareTypeID = softwareType.SoftwareTypeID;
        }
      },
    },
    purchasingVendor: {
      get() {
        return this.activeComputer.PurchasingVendor;
      },
      set(purchasingVendor) {
        if (purchasingVendor) {
          this.activeComputer.PurchasingVendor = _.cloneDeep(purchasingVendor);
          this.activeComputer.PurchasingVendorID = purchasingVendor.PurchasingVendorID;
        }
      },
    },
    allCustomers() {
      return this.$store.state.allCustomers.filter(
        (c) => c.CustomerID !== this.activeComputer.CustomerID,
      );
    },
  },
  watch: {
    '$store.state.computerModalFormOpen': function (open) {
      if (open) {
        this.refreshData();
      }
    },
  },
  methods: {
    closeComputerModal(skipDirtyCheck) {
      const confirm = () => {
        this.$store.commit('setComputerModalFormOpen', false);
        setTimeout(() => {
          this.$store.commit('setActiveComputer');
        }, 200);
      };

      if (this.$store.getters.isActiveComputerDirty && !skipDirtyCheck) {
        this.showConfirm(
          'Are you sure you want to leave this computer without saving?',
          'You have unsaved changes',
        ).then(confirm, _.noop);
      } else {
        confirm();
      }
    },
    saveComputer() {
      let promise = Promise.resolve();

      if (this.activeComputer.ComputerID) {
        promise = this.$store.dispatch('updateComputer');
      } else {
        promise = this.$store.dispatch('createComputer');
      }

      promise
        .then(() => this.closeComputerModal(true));
    },
    hideComputer() {
      const confirm = () => {
        this.$store.dispatch('deleteComputer')
          .then(() => {
            this.closeComputerModal(true);
          });
      };

      this.showConfirm(
        'Are you sure you want to hide this computer?',
        'Hide Computer',
      ).then(confirm, _.noop);
    },
    restoreComputer() {
      const confirm = () => {
        this.$store.dispatch('restoreComputer')
          .then(() => {
            this.closeComputerModal(true);
          });
      };

      this.showConfirm(
        'Are you sure you want to unhide this computer?',
        'Hide Computer',
      ).then(confirm, _.noop);
    },
    generateKey() {
      this.$store.dispatch('generateKey');
    },
    refreshData() {
      const softwareTypeParams = { params: { HiddenStatus: 'ShowNotHidden' } };
      this.$store.dispatch('getSoftwareTypes', softwareTypeParams);
      this.$store.dispatch('getPurchasingVendors');
    },
    openTransferComputerModal() {
      this.isComputerTransferModalOpen = true;
      this.$store.dispatch('getAllCustomers');
    },
    closeTransferComputerModal() {
      this.isComputerTransferModalOpen = false;
    },
    transferComputer() {
      const dto = {
        ComputerID: this.activeComputer.ComputerID,
        TransferToCustomerID: this.customer.CustomerID,
        ComputerRowVersion: this.activeComputer.ComputerRowVersion,
      };

      this.$store.dispatch('transferComputer', dto).then(() => {
        this.closeTransferComputerModal();
        this.closeComputerModal(true);
        EventBus.$emit('getCustomerComputers');
      });
    },
  },
};
</script>

<style lang="scss"></style>
